import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/outline';
import { ordersApi } from '../../services/api/orders.api';
import axios from '../../config/axios';

// مكون أيقونة الريال السعودي
const RiyalIcon = () => (
    <span style={{ fontFamily: 'saudi_riyal', fontSize: '1.2em' }}>&#xE900;</span>
);

export default function PaymentSuccess() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const orderId = searchParams.get('order_id') || searchParams.get('orderId');
    const [orderDetails, setOrderDetails] = useState(null);

    useEffect(() => {
        if (!orderId) {
            navigate('/');
            return;
        }

        ordersApi.getOrder(orderId)
            .then(response => {
                if (response.status === 'rejected') {
                    navigate(`/payment/error?order_id=${orderId}`);
                } else {
                    setOrderDetails(response);
                    
                    // تحديث قائمة المشتريات في sessionStorage
                    try {
                        const storedCourses = sessionStorage.getItem('dose_user_courses');
                        const currentCourses = storedCourses ? JSON.parse(storedCourses) : [];
                        
                        // استخراج معرفات المحتوى الجديد (كورسات أو كتب)
                        const newItems = response.items
                            .filter(item => item.course_id || item.course?.id || item.book_id || item.book?.id)
                            .map(item => item.course_id || item.course?.id || item.book_id || item.book?.id);
                        
                        if (newItems.length > 0) {
                            const updatedCourses = [...new Set([...currentCourses, ...newItems])];
                            sessionStorage.setItem('dose_user_courses', JSON.stringify(updatedCourses));
                            window.dispatchEvent(new Event('storage'));
                        }
                    } catch (error) {
                        console.error('خطأ في تحديث المشتريات:', error);
                    }
                }
            })
            .catch(error => {
                console.error('خطأ في جلب تفاصيل الطلب:', error);
            });
    }, [orderId, navigate]);

    const handleViewOrder = () => {
        navigate('/orders');
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6 flex flex-col justify-center">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 my-auto">
                {/* الفاتورة المقصوصة مع تأثير ظل */}
                <div className="relative bg-white dark:bg-gray-800 shadow-2xl rounded-lg overflow-hidden" style={{ boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}>
                    {/* الجزء المقصوص من الأعلى - شكل مثلثات */}
                    <div className="w-full h-6 bg-gray-100 dark:bg-gray-900 overflow-hidden">
                        <div className="flex w-full justify-between">
                            {[...Array(40)].map((_, i) => (
                                <div key={i} className="w-3 h-6 bg-white dark:bg-gray-800" style={{ clipPath: 'polygon(50% 0%, 0 100%, 100% 100%)', margin: '0 -1px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}></div>
                            ))}
                        </div>
                    </div>
                    
                    <div className="px-4 py-5 sm:p-6">
                        <div className="text-center">
                            <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500" />
                            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                                تمت عملية الدفع بنجاح
                            </h3>
                            <div className="mt-2 space-y-1">
                                <p className="text-sm text-gray-500 dark:text-gray-400">شكراً لك!</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">تم تأكيد طلبك وسيتم إضافة المنتجات لحسابك.</p>
                            </div>
                        </div>

                        <div className="mt-6 border-t border-dashed border-gray-200 dark:border-gray-700 pt-6">
                            <div className="space-y-4">
                                <div>
                                    <h4 className="text-base font-medium text-gray-900 dark:text-white">
                                        تفاصيل الطلب
                                    </h4>
                                    <dl className="mt-2 space-y-2">
                                        <div>
                                            <dt className="inline text-sm text-gray-500 dark:text-gray-400">رقم الطلب: </dt>
                                            <dd className="inline text-sm text-gray-900 dark:text-white">{orderId}</dd>
                                        </div>
                                        {orderDetails && (
                                            <>
                                                {orderDetails.total > 0 && (
                                                    <div>
                                                        <dt className="inline text-sm text-gray-500 dark:text-gray-400">المبلغ الإجمالي: </dt>
                                                        <dd className="inline text-sm text-gray-900 dark:text-white">{orderDetails.total} <RiyalIcon /></dd>
                                                    </div>
                                                )}
                                                {orderDetails.discount > 0 && (
                                                    <div>
                                                        <dt className="inline text-sm text-gray-500 dark:text-gray-400">الخصم: </dt>
                                                        <dd className="inline text-sm text-green-600 dark:text-green-400">{orderDetails.discount} <RiyalIcon /></dd>
                                                    </div>
                                                )}
                                                {orderDetails.final_total > 0 && (
                                                    <div>
                                                        <dt className="inline text-sm text-gray-500 dark:text-gray-400">المبلغ النهائي: </dt>
                                                        <dd className="inline text-sm font-bold text-gray-900 dark:text-white">{orderDetails.final_total} <RiyalIcon /></dd>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        <div>
                                            <dt className="inline text-sm text-gray-500 dark:text-gray-400">طريقة الدفع: </dt>
                                            <dd className="inline text-sm text-gray-900 dark:text-white">
                                                {orderDetails?.payment_method === 'tamara' ? 'تمارا' : 
                                                 orderDetails?.payment_method === 'installment' ? 'تقسيط' : 'دفع إلكتروني'}
                                            </dd>
                                        </div>
                                        {orderDetails?.installment && (
                                            <>
                                                <div className="border-t border-dashed border-gray-200 dark:border-gray-700 pt-2 mt-2">
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">تفاصيل التقسيط</p>
                                                </div>
                                                <div>
                                                    <dt className="inline text-sm text-gray-500 dark:text-gray-400">عدد الدفعات: </dt>
                                                    <dd className="inline text-sm text-gray-900 dark:text-white">{orderDetails.installment.installment_count}</dd>
                                                </div>
                                                <div>
                                                    <dt className="inline text-sm text-gray-500 dark:text-gray-400">الدفعة الأولى: </dt>
                                                    <dd className="inline text-sm text-gray-900 dark:text-white">{orderDetails.installment.paid_amount} <RiyalIcon /></dd>
                                                </div>
                                                <div>
                                                    <dt className="inline text-sm text-gray-500 dark:text-gray-400">المبلغ المتبقي: </dt>
                                                    <dd className="inline text-sm text-gray-900 dark:text-white">{orderDetails.installment.remaining_amount} <RiyalIcon /></dd>
                                                </div>
                                            </>
                                        )}
                                    </dl>
                                </div>
                            </div>
                        </div>

                        {orderDetails && orderDetails.coupon_code && (
                            <div className="mt-4 border-t border-dashed border-gray-200 dark:border-gray-700 pt-4">
                                <div className="flex justify-between mb-2">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">كود الخصم المستخدم:</span>
                                    <span className="text-sm text-gray-900 dark:text-white">{orderDetails.coupon_code}</span>
                                </div>
                            </div>
                        )}

                        {orderDetails && orderDetails.items && orderDetails.items.length > 0 && (
                            <div className="mt-6 border-t border-dashed border-gray-200 dark:border-gray-700 pt-4">
                                <div className="space-y-4">
                                    <div>
                                        <h4 className="text-base font-medium text-gray-900 dark:text-white">
                                            المنتجات
                                        </h4>
                                        <div className="mt-2">
                                            <div className="flex justify-between text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                                                <span>المنتج</span>
                                                <span>السعر</span>
                                            </div>
                                            {orderDetails.items.map((item) => (
                                                <div key={item.id} className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                                                    <span className="text-sm text-gray-800 dark:text-gray-200">
                                                        {item.book?.title || item.course?.title}
                                                    </span>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {item.price} <RiyalIcon />
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mt-6 flex justify-center space-x-4 space-x-reverse">
                            <button
                                onClick={() => navigate('/')}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                العودة للرئيسية
                            </button>
                            <button
                                onClick={() => navigate('/profile?tab=wallet')}
                                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                مدفوعاتي
                            </button>
                        </div>
                    </div>
                    
                    {/* الجزء المقصوص من الأسفل - شكل مثلثات */}
                    <div className="w-full h-6 bg-gray-100 dark:bg-gray-900 overflow-hidden">
                        <div className="flex w-full justify-between">
                            {[...Array(40)].map((_, i) => (
                                <div key={i} className="w-3 h-6 bg-white dark:bg-gray-800" style={{ clipPath: 'polygon(50% 100%, 0 0, 100% 0)', margin: '0 -1px', boxShadow: '0 -1px 2px rgba(0,0,0,0.1)' }}></div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
